/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import {
  getThemePreference,
  setResolvedThemeCookie,
  setThemePreference,
} from "@/lib/utils/design/theme-cookies";
import { useCallback, useEffect, useMemo, useState } from "react";

export type ThemeMode = "light" | "dark" | "system";
export type ResolvedMode = "light" | "dark";

function resolveMode(mode: ThemeMode): ResolvedMode {
  if (mode === "system") {
    return typeof window !== "undefined" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  }
  return mode;
}

function applyModeToDOM(resolved: ResolvedMode): void {
  const docEl = document.documentElement;
  docEl.setAttribute("data-theme", resolved);
  if (resolved === "dark") {
    docEl.classList.add("dark");
  } else {
    docEl.classList.remove("dark");
  }
  setResolvedThemeCookie(resolved);
}

/**
 * Single owner of light/dark/system mode.
 *
 * Source of truth: cookie (`theme-preference`).
 * On toggle: cookie → DOM → resolved cookie, all synchronous.
 * Blocking script in layout.tsx handles initial page load.
 */
export function useThemeMode() {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    if (typeof window === "undefined") return "light";
    const stored = getThemePreference();
    return stored === "light" || stored === "dark" || stored === "system"
      ? stored
      : "light";
  });

  const resolvedMode = useMemo<ResolvedMode>(() => resolveMode(mode), [mode]);

  const setMode = useCallback((newMode: ThemeMode) => {
    setModeState(newMode);
    setThemePreference(newMode);
    applyModeToDOM(resolveMode(newMode));
  }, []);

  // Listen for OS preference changes when mode is "system"
  useEffect(() => {
    if (mode !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => {
      applyModeToDOM(e.matches ? "dark" : "light");
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [mode]);

  return { mode, resolvedMode, setMode } as const;
}
